<template>
  <Listbox as="div" v-model="currentStore" class="flex items-center" v-slot="{ open }">
    <ListboxLabel class="block text-sm font-medium text-white"></ListboxLabel>
    <div class="relative w-full">
      <ListboxButton
        class="rounded-lg relative cursor-pointer border bg-white py-2 pl-3 text-left hover:bg-gray-100 sm:text-sm w-full"
        @click="handleButtonClick"
      >
        <div class="flex flex-row items-center">
          <OfficeBuildingIcon
            v-if="currentStore.id === 0"
            class="h-6 w-6 text-primary-dark bg-primary-light p-1 rounded-md shrink-0"
          />
          <HomeIcon v-else class="h-6 w-6 text-primary-dark bg-primary-light p-1 rounded-md shrink-0" />
          <div class="ml-2 block truncate flex-1">
            <template v-if="open">
              <div class="relative">
                <div class="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                  <SearchIcon class="h-3 w-3 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  ref="searchInput"
                  type="text"
                  class="w-full border-0 bg-transparent pl-5 pr-2 py-0 text-sm text-gray-800 focus:outline-hidden focus:ring-0"
                  placeholder="Rechercher..."
                  @input="debounceSearch"
                  @click.stop
                  :value="searchKeyword"
                />
              </div>
            </template>
            <template v-else>
              <span class="block truncate font-semibold text-sm pr-6 text-gray-800">
                {{ currentStore.salesChannel }}
              </span>
            </template>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
              <ChevronDownIcon class="h-5 w-5" aria-hidden="true" />
            </span>
          </div>
        </div>
      </ListboxButton>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        @after-enter="focusSearchInput"
      >
        <ListboxOptions
          class="absolute z-10 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm cursor-pointer"
        >
          <ListboxOption
            as="template"
            v-for="store in filteredStoresOptions"
            :key="store.id"
            :value="store"
            v-slot="{ active, selected }"
          >
            <li
              :class="[
                active ? 'bg-gray-200' : '',
                'relative select-none py-2 pl-3 pr-9 cursor-pointer flex items-center gap-2',
              ]"
            >
              <OfficeBuildingIcon v-if="store.id === 0" class="h-4 w-4 text-gray-500 shrink-0" />
              <HomeIcon v-else class="h-4 w-4 text-gray-500 shrink-0" />
              <span class="text-black text-sm block cursor-pointer">
                {{ store.salesChannel }}
              </span>
              <span
                v-if="selected"
                :class="[
                  active ? 'bg-gray-200' : '',
                  'absolute inset-y-0 right-0 flex items-center pr-4 cursor-pointer',
                ]"
              >
                <CheckIcon class="h-5 w-5 text-primary-dark" aria-hidden="true" />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import { CheckIcon, ChevronDownIcon } from "@heroicons/vue/solid";
import { SearchIcon, OfficeBuildingIcon, HomeIcon } from "@heroicons/vue/outline";
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue";
import { isPresent } from "@estaly/ui";

export default {
  components: {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
    CheckIcon,
    ChevronDownIcon,
    SearchIcon,
    OfficeBuildingIcon,
    HomeIcon,
  },

  data() {
    return {
      searchKeyword: "",
      debounce: null,
      stores: [],
    };
  },

  computed: {
    ...mapState("store", ["index", "selectedStoreId"]),
    ...mapGetters("store", ["selectedStore", "activeStores"]),

    storesOptions() {
      return [{ id: 0, salesChannel: "Admin" }, ...this.activeStores];
    },

    filteredStoresOptions() {
      if (!this.searchKeyword?.trim()) return this.storesOptions;

      const term = this.searchKeyword.toLowerCase();
      return this.storesOptions.filter((store) => {
        const salesChannel = store.salesChannel || "";
        return salesChannel.toLowerCase().includes(term);
      });
    },

    currentStore: {
      get() {
        return this.selectedStore || this.storesOptions[0];
      },

      set(value) {
        if (!isPresent(value)) return this.updateStoreId("");

        this.searchKeyword = "";
        this.$emit("close");
        this.redirect(value);
      },
    },
  },

  methods: {
    ...mapActions("store", ["updateStoreId", "fetchActiveStores", "fetch"]),

    redirect(currentStore) {
      if (currentStore.salesChannel === "Admin") {
        this.updateStoreId(currentStore.id);
        this.$router.push("/admin/home");
      } else if (currentStore.source === "offline") {
        this.updateStoreId(currentStore.id);
        this.fetch({ id: currentStore.id });
        this.$router.push("/pos/home");
      } else {
        this.updateStoreId(currentStore.id);
        this.fetch({ id: currentStore.id });
        this.$router.push(`/platform/products`);
      }
    },

    debounceSearch(event) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.searchKeyword = event.target.value;
      }, 300);
    },

    focusSearchInput() {
      this.$nextTick(() => {
        const input = this.$refs.searchInput;
        if (input && typeof input.focus === "function") input.focus();
      });
    },

    handleButtonClick(event) {
      // Prevent closing if clicking on the input
      if (event.target.tagName === "INPUT") event.stopPropagation();
    },
  },

  async beforeMount() {
    await this.fetchActiveStores({ perPage: 2000 });
    this.stores = this.index;
  },

  mounted() {
    // When path is updated to /, the router redirects to the home page.
    // If the home page is a admin page but a store was selected, here we redirect to the store page.
    if (this.$route.meta.layout === "admin-layout") {
      if (this.currentStore?.id !== 0 && this.currentStore?.id !== "") {
        this.redirect(this.currentStore);
      }
    }
  },
};
</script>
